import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Table from "@material-ui/core/Table";
import _ from "lodash";
import memoizeOne from "memoize-one";
import * as React from "react";
import SearchBar, { ISearchResult, searchFilterMeta } from "./SearchBar";
import EnhancedTableHeader from "./smartTable/EnhancedTableHeader";
import IRow from "./smartTable/IRow";
import SmartTableBody from "./smartTable/SmartTableBody";

export interface ITableHeader<T> {
  label: string;
  id: string;
  orderBy?: (item: T) => object; // Returns a thing which lodash uses to compare to determine ordering.
  notSearchable?: boolean;
  render: (item: T) => React.ReactNode;
}

export interface ITableSort {
  order: "asc" | "desc";
  orderBy: string;
}

interface ITableStyles {
  headerColor: string;
  color1: string;
  color2: string;
}

interface ISmartTableProps<T> {
  tableHeaders: Array<ITableHeader<T>>;
  tableRows: T[];
  default: ITableSort;
  filterable: boolean;
  sortable: boolean;
  searchable: boolean;
  showSearchNumbers: boolean;
  tableStyle?: ITableStyles;
}

interface ISmartTableState extends ITableSort {
  search: string;
  filterBy: string;
  filter: string;
}

export default class SmartTable<T extends IRow> extends React.Component<
  ISmartTableProps<T>,
  ISmartTableState
> {
  public state = {
    filter: "",
    filterBy: "",
    order: this.props.default.order,
    orderBy: this.props.default.orderBy,
    search: "",
  };

  private lastIdenticalOrderedFilteredTableRows: Array<ISearchResult<T>> = [];

  private memoizedSearchableTableHeaders = memoizeOne(
    (tableHeaders: Array<ITableHeader<T>>): string[] =>
      tableHeaders.filter((h) => !h.notSearchable).map((h) => h.id)
  );

  private memoizedFilteredTableRows = memoizeOne(
    (tableRows: T[], query: string, searchableTableHeaders: string[]) =>
      searchFilterMeta(tableRows, query, searchableTableHeaders)
  );

  private memoizedTableHeaders = memoizeOne(
    (rawTableHeaders) => rawTableHeaders
  );

  private memoizedOrderedFilteredTableRows = memoizeOne(
    (
      filteredTableRows: Array<ISearchResult<T>>,
      tableHeaders: Array<ITableHeader<T>>,
      orderBy: ITableSort["orderBy"],
      order: ITableSort["order"],
      paginate: boolean
    ) => {
      const tableHeader = tableHeaders.find((h) => h.id === orderBy);
      if (!tableHeader) {
        throw Error(
          `Header with id ${orderBy} not found. You must pass in a default orderBy that refers to an actual header.`
        );
      }

      return _.orderBy(
        filteredTableRows,
        [
          (info: ISearchResult<T>) =>
            tableHeader.orderBy ? tableHeader.orderBy(info.item) : true,
        ],
        [order]
      ); // .filter((info) => paginate ? info.show : true);
    }
  );

  // Need to do custom stuff to avoid having an output array with a new identity
  // after re-filtering.
  private memoizedIdenticalOrderedFilteredTableRows = memoizeOne(
    (orderedFilteredTableRows: Array<ISearchResult<T>>) => {
      const getIsDifferent = () => {
        if (
          this.lastIdenticalOrderedFilteredTableRows.length !==
          orderedFilteredTableRows.length
        ) {
          return true;
        }
        const zipped = _.zip(
          this.lastIdenticalOrderedFilteredTableRows,
          orderedFilteredTableRows
        );
        if (
          _.some(
            zipped,
            (pair) =>
              pair[0]!.item !== pair[1]!.item || pair[0]!.show !== pair[1]!.show
          )
        ) {
          return true;
        }
        return false;
      };
      if (getIsDifferent()) {
        this.lastIdenticalOrderedFilteredTableRows = orderedFilteredTableRows;
      }
      return this.lastIdenticalOrderedFilteredTableRows;
    }
  );

  public tableRows() {
    return this.props.tableRows;
  }

  public tableHeaders() {
    return this.memoizedTableHeaders(this.props.tableHeaders);
  }

  public searchableTableHeaders() {
    return this.memoizedSearchableTableHeaders(this.tableHeaders());
  }

  public filteredTableRows() {
    const { filter, search } = this.state;
    return this.memoizedFilteredTableRows(
      this.tableRows(),
      `${search}${filter}`,
      this.searchableTableHeaders()
    );
  }

  public orderedFilteredTableRows() {
    return this.memoizedOrderedFilteredTableRows(
      this.filteredTableRows(),
      this.tableHeaders(),
      this.state.orderBy,
      this.state.order,
      false
    );
  }

  public identicalOrderedFilteredTableRows() {
    return this.memoizedIdenticalOrderedFilteredTableRows(
      this.orderedFilteredTableRows()
    );
  }

  // Gets the values underneath headers, no repeats
  // We are assuming all things are strings
  public valuesUnderHeader(header: string): string[] {
    const values = new Set<string>(); // Set prevents duplicates
    for (const row of this.tableRows()) {
      // TODO: Fix TypeScript flagged issue
      // @ts-ignore
      this.expandArray(row[header], values);
    }

    return Array.from(values);
  }

  // Rather than showing "id" it will show the useful label
  public getHeaderLabel(header: string): string {
    for (const tableHeader of this.props.tableHeaders) {
      if (tableHeader.id === header) {
        return tableHeader.label;
      }
    }
    return "";
  }

  public handleSort = (newOrder: any) => this.setState({ ...newOrder });

  public handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({ search: e.target.value });

  public handleFilterByChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    this.setState({ filterBy: e.target.value, filter: "" });

  public handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    this.setState({ filter: e.target.value });

  public handleClearQuery = () => {
    this.setState({ filter: "", filterBy: "", search: "" });
  };

  public render() {
    const { filterable, searchable, sortable, tableHeaders, tableStyle } =
      this.props;
    const { filter, filterBy, order, orderBy, search } = this.state;
    return (
      <div>
        <Grid
          style={{ marginBottom: 4 }}
          container={true}
          justifyContent="flex-start"
          alignItems="flex-end"
          spacing={2}
        >
          {searchable && (
            <Grid item={true}>
              <SearchBar search={search} onChange={this.handleSearchChange} />
            </Grid>
          )}
          {filterable && (
            <Grid item={true}>
              <FormControl>
                <InputLabel htmlFor="filter-by">Filter by</InputLabel>
                <Select
                  style={{ width: "10em" }}
                  inputProps={{
                    id: "filter-by",
                    name: "filter by",
                  }}
                  value={filterBy}
                  onChange={this.handleFilterByChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {this.searchableTableHeaders().map(
                    (header: string, i: number) => (
                      <MenuItem key={i} value={header}>
                        {this.getHeaderLabel(header)}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          {filterBy && (
            <Grid item={true}>
              <FormControl>
                <InputLabel htmlFor="filter">
                  {this.getHeaderLabel(filterBy)}
                </InputLabel>
                <Select
                  style={{ width: "10em" }}
                  inputProps={{
                    id: "filter",
                    name: "filter",
                  }}
                  value={filter}
                  onChange={this.handleFilterChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {this.valuesUnderHeader(filterBy).map(
                    (value: string, i: number) => (
                      <MenuItem key={i} value={value}>
                        {value}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          {searchable || filterable ? (
            <Grid item={true}>
              <Button onClick={this.handleClearQuery}>Clear</Button>
            </Grid>
          ) : null}
        </Grid>
        <Table style={{ borderRadius: 5, overflow: "hidden" }}>
          <EnhancedTableHeader
            columnData={tableHeaders}
            sortable={sortable}
            order={order}
            orderBy={orderBy}
            onRequestSort={this.handleSort}
            color={tableStyle ? tableStyle.headerColor : "white"}
          />
          <SmartTableBody
            rows={this.identicalOrderedFilteredTableRows()}
            headers={this.tableHeaders()}
            alternatingColors={
              tableStyle ? [tableStyle.color1, tableStyle.color2] : undefined
            }
          />
        </Table>
      </div>
    );
  }

  // Expands an array and its subarrays into a set
  private expandArray(item: any[] | string, s: Set<string>) {
    if (Array.isArray(item)) {
      for (const i of item) {
        this.expandArray(i, s);
      }
    } else {
      s.add(item);
    }
  }
}



// WEBPACK FOOTER //
// ./src/shared/components/SmartTable.tsx